// EtaChip — hero ETA + pace badge + heartbeat for the Monitor.
//
// Three lines, top-right of the hero, replacing the bare "Provisioning…"
// status text. Designed to set expectations on the long-running phases:
//
//   ~14 min remaining · 6 min behind baseline   <- ETA + pacing badge
//   ● 4s ago                                     <- heartbeat (live tick)
//
// The heartbeat ticks via a 1Hz setInterval — same cost as the existing
// Monitor poll; cheap. Goes amber at >60s and red at >180s so a stalled
// worker is visually obvious without the user reading the stale banner.
//
// ETA confidence ladder (mirrors app/services/pace.py):
//   * heuristic      — 0 prior runs; ETA from static volume budget. Pill:
//                      "estimate · no history yet".
//   * early_baseline — 1-2 prior runs; ETA from observed durations but
//                      we don't show on-pace/behind verdict. Pill:
//                      "early estimate (N of 3)".
//   * on_pace / slightly_behind / behind — full verdict.
//   * no_baseline    — legacy fallback (older backend); rendered same as
//                      heuristic so the chip never goes back to a bare
//                      "Provisioning…" string.

import { useEffect, useState } from "react";
import type { JobPace } from "@/lib/types";

interface EtaChipProps {
  pace: JobPace | null;
  lastUpdatedAt: string | null;
  fallbackStatus: string;
}

function fmtMin(sec: number): string {
  if (sec < 60) return `~${Math.max(1, Math.round(sec))}s`;
  return `~${Math.round(sec / 60)} min`;
}

interface PaceBadge {
  text: string;
  color: string;
  title?: string;
}

// Sync this with MIN_BASELINE_RUNS in app/services/pace.py.
// Backend now also surfaces `pace.min_baseline_runs`, so we prefer that
// when present and only fall back to this constant for old payloads.
const FALLBACK_MIN_BASELINE_RUNS = 3;

function paceBadge(pace: JobPace): PaceBadge | null {
  const minRuns = pace.min_baseline_runs ?? FALLBACK_MIN_BASELINE_RUNS;

  if (pace.pace_status === "heuristic" || pace.pace_status === "no_baseline") {
    return {
      text: "estimate · no history yet",
      color: "#9FBFD9",
      title:
        "First time we've seen this pack + volume + merge mode. ETA comes from a default budget; it gets sharper after the first successful run completes and is shared across all SEs.",
    };
  }
  if (pace.pace_status === "early_baseline") {
    const have = pace.baseline_runs_count ?? 0;
    return {
      text: `early estimate (${have} of ${minRuns})`,
      color: "#9FBFD9",
      title: `Based on ${have} prior successful run${have === 1 ? "" : "s"}. After ${minRuns} runs the chip switches to on-pace / behind-baseline judgement.`,
    };
  }
  if (pace.pace_status === "on_pace") {
    return { text: "on pace", color: "#7CDB8F" };
  }
  const expected = pace.expected_total_sec ?? 0;
  const behindBy = Math.max(0, pace.elapsed_sec - expected);
  const fmt = fmtMin(behindBy);
  if (pace.pace_status === "slightly_behind") {
    return { text: `${fmt} behind baseline`, color: "#F2C94C" };
  }
  return { text: `${fmt} behind baseline`, color: "#FF6B6B" };
}

export function EtaChip({
  pace,
  lastUpdatedAt,
  fallbackStatus,
}: EtaChipProps): JSX.Element {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const heartbeatSec = lastUpdatedAt
    ? Math.max(0, Math.floor((now - new Date(lastUpdatedAt).getTime()) / 1000))
    : null;

  // Stale-thresholds match the backend's progress.py STALE_THRESHOLDS in
  // intent: "should the user be worried?" >60s amber, >180s red.
  const heartbeatColor =
    heartbeatSec === null
      ? "#AACBE2"
      : heartbeatSec > 180
        ? "#FF6B6B"
        : heartbeatSec > 60
          ? "#F2C94C"
          : "#7CDB8F";

  // We render an ETA whenever the backend gives us expected_remaining_sec,
  // regardless of confidence tier. The accompanying badge tells the user
  // how much to trust it.
  //
  // KI-LOW (2026-04-21, second pass): a few months of dogfooding
  // surfaced two cases the original guard missed —
  //
  //   1. ``early_baseline`` runs (1–2 prior runs of any size) can
  //      produce wildly off ETAs when the historical runs differed
  //      meaningfully in size from the live one. Hide their headline
  //      claim under the same <60s threshold we already use for
  //      pure heuristic.
  //   2. Pure heuristic mode (no prior runs at all) shouldn't
  //      regenerate "Provisioning…" indefinitely — that loses the
  //      sense-of-progress the chip is supposed to convey. Show
  //      "X min elapsed · estimating…" instead so the user sees the
  //      wall-clock advance even when we have nothing to predict
  //      against. Per docs/KNOWN_ISSUES.md option (C).
  const noBaseline =
    pace?.pace_status === "heuristic" ||
    pace?.pace_status === "no_baseline";
  const lowConfidence =
    noBaseline || pace?.pace_status === "early_baseline";
  const remainingTooLowToTrust =
    pace?.expected_remaining_sec != null &&
    pace.expected_remaining_sec < 60 &&
    lowConfidence;

  let etaText: string;
  if (!pace) {
    etaText = fallbackStatus;
  } else if (noBaseline) {
    etaText = `${fmtMin(pace.elapsed_sec)} elapsed`;
  } else if (pace.expected_remaining_sec != null && !remainingTooLowToTrust) {
    etaText = `${fmtMin(pace.expected_remaining_sec)} remaining`;
  } else {
    etaText = fallbackStatus;
  }

  const badge = pace ? paceBadge(pace) : null;
  // Confidence-tier pills get a subtle pill background to read as
  // "qualifier" rather than judgement; pace verdicts stay inline.
  const isQualifierBadge =
    pace?.pace_status === "heuristic" ||
    pace?.pace_status === "early_baseline" ||
    pace?.pace_status === "no_baseline";

  return (
    <div
      style={{
        fontSize: 11,
        color: "#AACBE2",
        marginTop: 4,
        lineHeight: 1.5,
        textAlign: "right",
      }}
    >
      <div>
        {etaText}
        {badge ? (
          <>
            {" · "}
            {isQualifierBadge ? (
              <span
                style={{
                  color: badge.color,
                  padding: "1px 6px",
                  borderRadius: 999,
                  background: "rgba(170,203,226,0.12)",
                  fontSize: 10,
                  letterSpacing: "0.02em",
                }}
                title={badge.title}
              >
                {badge.text}
              </span>
            ) : (
              <span style={{ color: badge.color }} title={badge.title}>
                {badge.text}
              </span>
            )}
          </>
        ) : null}
      </div>
      {heartbeatSec !== null && (
        <div style={{ fontSize: 11, fontWeight: 500 }}>
          <span
            style={{
              color: heartbeatColor,
              fontSize: 13,
              verticalAlign: "middle",
              marginRight: 3,
            }}
          >
            ●
          </span>
          {heartbeatSec === 0 ? "just now" : `updated ${heartbeatSec}s ago`}
        </div>
      )}
    </div>
  );
}
